<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { scrollLock } from "@magnit/core/src/helpers/scrollLock";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import VInlineButton from "@magnit/core/src/components/VInlineButton/VInlineButton.vue";
import SortingIcon from "@magnit/icons/src/assets/icons/sort-arrows-24.svg";
import SortingArrowIcon from "@magnit/icons/src/assets/icons/arrow-up-full-16.svg";
import VCatalogSortingModal from "./VCatalogSortingModal.vue";
import type {
  ISortingProps,
  ISortingValue,
} from "@magnit/unit-catalog/src/unit-catalog.types";
import { reverseOrder } from "../utils/sorting";
import { useAnalyticManager } from "@magnit/analytic-events";

const props = defineProps<ISortingProps>();
const emit = defineEmits<{
  "update:modelValue": [sortingModel: ISortingValue];
}>();
const { send: nativeSend } = useAnalyticManager();
const send = props.analyticsSender || nativeSend;
const showSortingModal = ref(false);
const reversibleItems = computed<string[]>(() =>
  props.configDesktop.reduce((acc: string[], el) => {
    if (el.reversible) acc.push(el.value);
    return acc;
  }, []),
);
const onButtonClick = () => {
  if (props.disabled) return;
  showSortingModal.value = true;
  send("CatalogListing:Sort:Click", {
    ...props.analyticsPayload,
  });
};
const getOrder = (item: ISortingValue) => {
  // для mobile и tablet приходит order (из модалки)
  if (item.order) return item.order;
  // для desktop не приходит order, надо вычислить
  return reversibleItems.value.includes(item.value) &&
    item.value === props.modelValue.value
    ? reverseOrder(props.modelValue.order!)
    : "desc";
};
const onChange = (item: ISortingValue) => {
  if (props.disabled) return;
  emit("update:modelValue", { value: item.value, order: getOrder(item) });
  send("CatalogListing:Sort:Variant:Click", {
    sortVariant: `${item.value}&${getOrder(item)}`,
    ...props.analyticsPayload,
  });
  onCloseModal();
};
const onCloseModal = () => {
  showSortingModal.value = false;
};

watch(
  () => showSortingModal.value,
  (next) => {
    next ? scrollLock.enable() : scrollLock.disable();
    if (next) {
      send("CatalogListing:Sort:View", {
        ...props.analyticsPayload,
      });
    }
  },
);
</script>

<template>
  <div class="catalog-sorting" data-test-id="sorting">
    <!--  Desktop  -->
    <VHidden :hide="{ xs: true, l: false }">
      <div class="catalog-sorting__knobs">
        <VText
          v-for="(item, index) in configDesktop"
          :key="index"
          :color="
            disabled
              ? 'tertiary'
              : modelValue.value === item.value
                ? 'primary'
                : 'secondary'
          "
          class="catalog-sorting__item"
          :class="{ 'catalog-sorting__item--disabled': disabled }"
          font="body-large-accent"
          @click="onChange(item)"
        >
          {{ item.title }}
          <VIcon
            v-if="
              !disabled &&
              modelValue.value === item.value &&
              reversibleItems.includes(item.value)
            "
            class="catalog-sorting__icon"
            :class="{
              'catalog-sorting__icon_desc': modelValue.order === 'desc',
            }"
            color="inherit"
            size="m"
          >
            <SortingArrowIcon />
          </VIcon>
        </VText>
      </div>
    </VHidden>
    <!--  end Desktop  -->
    <!--  Mobile/Tablet  -->
    <VHidden :hide="{ l: true }">
      <VInlineButton
        class="catalog-sorting__button"
        :disabled="disabled"
        theme="secondary"
        icon-position="left"
        size="l"
        @click="onButtonClick"
      >
        <template #icon>
          <VIcon color="inherit">
            <SortingIcon />
          </VIcon>
        </template>
        Сортировка
        <div
          v-if="
            props.modelValue.value !== props.defaultSortValue ||
            props.modelValue.order !== 'desc'
          "
          class="catalog-sorting__button-mark"
        />
      </VInlineButton>
      <VCatalogSortingModal
        :disabled="disabled"
        :open="showSortingModal"
        :model-value="modelValue"
        :config="configMobile"
        @update:model-value="onChange"
        @close="onCloseModal"
      />
    </VHidden>
    <!--  end Mobile/Tablet  -->
  </div>
</template>

<style lang="postcss" scoped>
.catalog-sorting {
  &__knobs {
    @media (--pl-viewport-from-ml) {
      display: flex;
      gap: var(--pl-unit-x4);
      align-items: center;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--pl-unit-x1) 0;
    cursor: pointer;

    &--disabled {
      cursor: not-allowed;
    }

    @media (--pl-viewport-from-l) {
      text-transform: lowercase;
    }
  }

  &__icon {
    display: flex;
    margin-left: var(--pl-unit-x1);

    &_desc {
      transform: scaleY(-1);
    }
  }

  &__button {
    position: relative;
  }

  &__button-mark {
    position: absolute;
    display: block;
    background: var(--pl-project-core-primary);
    border-radius: 50%;
    width: var(--pl-unit-x2);
    height: var(--pl-unit-x2);
    top: -2px;
    right: -2px;
    transform: translateX(50%);
  }
}
</style>
